import { useState } from "react";
import type { Signal } from "../api";

/** How many signals show before the rest fold away. They arrive loudest
 *  first, so the fold only ever hides the quieter ones. */
const SHOWN = 4;

/**
 * What needs attention.
 *
 * Every line carries the arithmetic behind it — observed against baseline,
 * and how many same-weekday samples that baseline came from. A finding
 * whose numbers are not shown is one nobody can check, and checking is the
 * whole reason detection is deterministic rather than a model's opinion.
 *
 * Severity is never colour alone: each row carries its label as text, so
 * the panel reads the same in greyscale, in forced-colors mode, and to
 * anybody who cannot separate the hues.
 */
export default function Signals({ signals, days }: { signals: Signal[]; days: number }) {
  const [open, setOpen] = useState(false);
  if (signals.length === 0) {
    return (
      <div className="signals quiet">
        <strong>Nothing needs attention</strong> in the last {days} complete days —
        every campaign is inside its usual range for the day of week.
      </div>
    );
  }

  return (
    <div className="signals">
      <div className="signals-head">
        Needs attention
        <span className="dim">
          {" "}
          · {signals.length} · last {days} complete days
        </span>
      </div>
      <ul>
        {(open ? signals : signals.slice(0, SHOWN)).map((s, i) => (
          <li key={`${s.campaign_id}-${s.kind}-${i}`} className={`sig ${s.severity}`}>
            <span className={`sev ${s.severity}`}>{SEVERITY[s.severity]}</span>
            <div className="sig-body">
              <div className="sig-text">{s.describe}</div>
              <div className="sig-math">
                {s.metric} · observed {round(s.observed)} vs baseline {round(s.baseline)}
                {s.baseline_samples > 0 && ` · ${s.baseline_samples} same-weekday samples`}
                {s.kind === "trigger_failure_burst" && " · platform fault"}
              </div>
            </div>
          </li>
        ))}
      </ul>
      {signals.length > SHOWN && (
        <button className="signals-more" onClick={() => setOpen(!open)} aria-expanded={open}>
          {open ? "Show fewer" : `Show ${signals.length - SHOWN} more`}
        </button>
      )}
    </div>
  );
}

const SEVERITY: Record<Signal["severity"], string> = {
  critical: "Critical",
  serious: "Serious",
  warning: "Warning",
  info: "Note",
};

/** Whole numbers for counts, two decimals for the rates and ladders. */
const round = (v: number): string =>
  Math.abs(v) >= 100 || Number.isInteger(v) ? Math.round(v).toLocaleString() : v.toFixed(2);
